import React, { useState } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const STATUS_COLORS = {
  'Not Started': 'default',
  'In Progress': 'info',
  'On Hold': 'warning',
  'Completed': 'success',
  'Cancelled': 'error'
};

export default function Jobs() {
  const navigate = useNavigate();
  const { jobs, deleteJob } = useData();
  const { canViewAllJobs, canCreateJobs } = usePermissions();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Filter jobs by role: Tech users only see jobs assigned to them
  const roleFilteredJobs = canViewAllJobs 
    ? jobs // Admins/managers see all jobs
    : jobs.filter(job => 
        // Tech users only see jobs assigned to them
        job.assignedTo === currentUser?.uid || 
        job.assignedTo === currentUser?.email ||
        (Array.isArray(job.assignedTo) && job.assignedTo.includes(currentUser?.uid)) ||
        (Array.isArray(job.assignedTo) && job.assignedTo.includes(currentUser?.email))
      );

  const filteredJobs = roleFilteredJobs.filter(job =>
    job.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRowClick = (job) => {
    navigate(`/jobs/${job.id}`);
  };

  const handleContextMenu = (event, job) => {
    event.preventDefault();
    setSelectedJob(job);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedJob(null);
  };

  const handleEdit = () => {
    if (selectedJob) {
      navigate(`/jobs/${selectedJob.id}/edit`);
    }
    handleCloseContextMenu();
  };

  const handleDelete = async () => {
    if (selectedJob) {
      try {
        await deleteJob(selectedJob.id);
      } catch (error) {
        console.error('Error deleting job:', error);
      }
    }
    handleCloseContextMenu();
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Jobs
        </Typography>
        {canCreateJobs && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/jobs/new')}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create New Job
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        label="Search Jobs"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Job ID</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Customer</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Description</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Total</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Start Date</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>End Date</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.map((job) => (
              <TableRow 
                key={job.id}
                onClick={() => handleRowClick(job)}
                onContextMenu={(e) => handleContextMenu(e, job)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <TableCell>{job.jobId}</TableCell>
                <TableCell>{job.customerName}</TableCell>
                <TableCell>{job.description}</TableCell>
                <TableCell>${job.total?.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip
                    label={job.status}
                    color={STATUS_COLORS[job.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(job.startDate)}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(job.endDate)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, job);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
} 